import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Search, Filter, Download, RefreshCcw } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { getAllVoucherHistory, VoucherHistory } from "@/lib/db/voucherDb";

const VoucherHistoryTab: React.FC = () => {
  const [history, setHistory] = useState<VoucherHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    outlet: "all",
    status: "all",
    startDate: "",
    endDate: ""
  });
  const { toast } = useToast();

  useEffect(() => {
    loadVoucherHistory();
  }, []);

  const loadVoucherHistory = async () => {
    setIsLoading(true);
    try {
      const allHistory = await getAllVoucherHistory();
      setHistory(allHistory);
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading voucher history:", error);
      toast({
        variant: "destructive",
        title: "Failed to load voucher history",
        description: "Could not retrieve voucher redemption data. Please try again."
      });
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      outlet: "all",
      status: "all",
      startDate: "",
      endDate: ""
    });
  };

  const handleExportCSV = () => {
    // Implementation for CSV export would go here
    toast({
      title: "Export started",
      description: "Voucher history data is being exported as CSV."
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "Fully Used":
        return "secondary";
      case "Partially Used":
        return "default"; // Changed from "success" to "default"
      case "Expired":
        return "destructive";
      default:
        return "default";
    }
  };

  const filteredHistory = history.filter(record => {
    // Apply search filter
    const searchFields = [
      record.customerName.toLowerCase(),
      record.voucherCode.toLowerCase(),
      record.cashierName.toLowerCase()
    ];
    
    const matchesSearch = searchTerm === "" || 
      searchFields.some(field => field.includes(searchTerm.toLowerCase()));
    
    // Apply other filters
    const matchesOutlet = filters.outlet === "all" || record.outlet === filters.outlet;
    const matchesStatus = filters.status === "all" || record.status === filters.status;
    
    // Apply date filters
    let matchesDates = true;
    if (filters.startDate) {
      const startDate = new Date(filters.startDate).getTime();
      matchesDates = matchesDates && record.redemptionDate >= startDate;
    }
    if (filters.endDate) {
      const endDate = new Date(filters.endDate).getTime() + 86400000; // Add one day to include the end date fully
      matchesDates = matchesDates && record.redemptionDate <= endDate;
    }
    
    return matchesSearch && matchesOutlet && matchesStatus && matchesDates;
  });

  return (
    <div className="space-y-4">
      {/* Search and Filters Bar */}
      <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, voucher code or cashier..."
            className="pl-8"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={loadVoucherHistory}
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-muted/40 p-4 rounded-md border border-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label htmlFor="outlet">Outlet</Label>
              <select
                id="outlet"
                className="w-full border border-input rounded-md h-10 px-3 bg-background"
                value={filters.outlet}
                onChange={(e) => handleFilterChange("outlet", e.target.value)}
              >
                <option value="all">All Outlets</option>
                <option value="Main Branch">Main Branch</option>
                <option value="East Branch">East Branch</option>
                <option value="West Branch">West Branch</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="w-full border border-input rounded-md h-10 px-3 bg-background"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Fully Used">Fully Used</option>
                <option value="Partially Used">Partially Used</option>
                <option value="Expired">Expired</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="startDate">Redemption Date From</Label>
              <Input
                type="date"
                id="startDate"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="endDate">To</Label>
              <Input
                type="date"
                id="endDate"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </div>
      )}
      
      {/* Voucher History Table */}
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Redeemed By</TableHead>
              <TableHead>Voucher Code</TableHead>
              <TableHead>Redemption Date</TableHead>
              <TableHead>Amount (RM)</TableHead>
              <TableHead>Products/Categories</TableHead>
              <TableHead>Outlet</TableHead>
              <TableHead>Terminal</TableHead>
              <TableHead>Cashier</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  Loading voucher history...
                </TableCell>
              </TableRow>
            ) : filteredHistory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  No voucher history found
                </TableCell>
              </TableRow>
            ) : (
              filteredHistory.map((record) => (
                <TableRow key={record.transactionId}>
                  <TableCell className="font-medium">{record.customerName}</TableCell>
                  <TableCell className="font-mono">{record.voucherCode}</TableCell>
                  <TableCell>{format(new Date(record.redemptionDate), "PPP p")}</TableCell>
                  <TableCell>RM {record.redemptionAmount.toFixed(2)}</TableCell>
                  <TableCell>
                    {record.products.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {record.products.map((product, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {product}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">No products recorded</span>
                    )}
                  </TableCell>
                  <TableCell>{record.outlet}</TableCell>
                  <TableCell>{record.terminal}</TableCell>
                  <TableCell>{record.cashierName}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(record.status) as "default" | "secondary" | "destructive" | "outline"}>
                      {record.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* No history message */}
      {!isLoading && history.length === 0 && (
        <div className="text-center p-8">
          <p className="text-muted-foreground mb-4">No voucher redemptions have been recorded yet.</p>
          <Button onClick={() => window.history.back()}>Return to Voucher Management</Button>
        </div>
      )}
    </div>
  );
};

export default VoucherHistoryTab;
